import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useLanguage } from '../../contexts/LanguageContext';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDark, setIsDark] = useState(() => document.documentElement?.classList?.contains('dark'));
  const { user, userProfile, signOut } = useAuth();
  const { role, isAdmin, isViewer, isGuest, can } = useRBAC();
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location?.pathname]);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement?.classList?.toggle('dark', next);
    try { localStorage.setItem('theme', next ? 'dark' : 'light'); } catch(e) {}
  };

  // Build navigation based on role
  const navigationItems = [
    // Reservations section - all authenticated users
    {
      section: t?.nav?.residents || 'Reservations',
      items: [
        { label: t?.nav?.reservationRequests || 'Reservation Request', path: '/reservation-request', icon: 'ClipboardList' },
      ],
    },

    // Operations - admin only
    ...(isAdmin ? [{
      section: t?.nav?.operations || 'Operations',
      items: [
        { label: t?.nav?.occupancyAnalytics || 'Occupancy Analytics', path: '/occupancy-analytics', icon: 'Users' },
        { label: t?.nav?.checkinCheckout || 'Check-in / Check-out', path: '/resident-checkin', icon: 'ClipboardCheck' },
        { label: t?.nav?.roomManagement || 'Room Management', path: '/room-management', icon: 'DoorOpen' },
        { label: t?.nav?.calendarView || 'Calendar View', path: '/calendar-view', icon: 'CalendarDays' },
        { label: t?.nav?.emailTemplateEditor || 'Email Templates', path: '/email-template-editor', icon: 'Mail' },
        { label: language === 'fr' ? 'Réclamations / Incidents' : 'Claims / Incidents', path: '/claims-dashboard', icon: 'FileWarning' },
        { label: language === 'fr' ? 'Restaurant' : 'Restaurant', path: '/restaurant', icon: 'UtensilsCrossed' },
        { label: 'Meal Ticket System', path: '/meal-ticket-system', icon: 'Ticket' },
      ],
    }] : []),

    // Operations - viewer (read-only subset)
    ...(isViewer ? [{
      section: t?.nav?.operations || 'Operations',
      items: [
        { label: t?.nav?.occupancyAnalytics || 'Occupancy Analytics', path: '/occupancy-analytics', icon: 'Users' },
        { label: t?.nav?.checkinCheckout || 'Check-in / Check-out', path: '/resident-checkin', icon: 'ClipboardCheck' },
        { label: t?.nav?.calendarView || 'Calendar View', path: '/calendar-view', icon: 'CalendarDays' },
        { label: language === 'fr' ? 'Restaurant' : 'Restaurant', path: '/restaurant', icon: 'UtensilsCrossed' },
        { label: 'Meal Ticket System', path: '/meal-ticket-system', icon: 'Ticket' },
      ],
    }] : []),

    // Analytics - admin and viewer only
    ...(can('canViewAnalytics') ? [{
      section: t?.nav?.analytics || 'Analytics',
      items: [
        { label: t?.nav?.advancedReporting || 'Advanced Reports', path: '/advanced-reporting', icon: 'FileBarChart' },
        { label: language === 'fr' ? 'Performance Bâtiments' : 'Building Performance', path: '/building-performance', icon: 'BarChart3' },
      ],
    }] : []),

    // Stay History - admin and viewer only (hidden from guests)
    ...(can('canViewStayHistory') ? [{
      section: language === 'fr' ? 'Historique' : 'History',
      items: [
        { label: language === 'fr' ? 'Historique Séjours' : 'Stay History', path: '/stay-history', icon: 'History' },
      ],
    }] : []),

    // Resident Services - visible to ALL authenticated users (guests, viewers, admins)
    {
      section: language === 'fr' ? 'Services Résidents' : 'Resident Services',
      items: [
        { label: language === 'fr' ? 'Objets Perdus' : 'Lost & Found', path: '/lost-found', icon: 'PackageSearch' },
        { label: language === 'fr' ? 'Plan du Campus' : 'Campus Map', path: '/campus-map', icon: 'Map' },
        { label: language === 'fr' ? 'Contacts' : 'Contacts', path: '/emergency-contacts', icon: 'Phone' },
        { label: language === 'fr' ? 'Centre d\'Aide' : 'Help Center', path: '/help-center', icon: 'HelpCircle' },
        { label: language === 'fr' ? 'Notifications' : 'Notifications', path: '/notification-center', icon: 'Bell' },
      ],
    },

    // Administration - admin only
    ...(isAdmin ? [{
      section: 'Administration',
      items: [
        { label: 'Resident Services Admin', path: '/resident-services-admin', icon: 'LayoutGrid' },
        { label: 'User Management', path: '/user-management', icon: 'UsersRound' },
        { label: language === 'fr' ? 'Importation en masse' : 'Bulk Import', path: '/bulk-import', icon: 'FileUp' },
      ],
    }] : []),
  ];

  const isActive = (path) => location?.pathname === path;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const roleColors = {
    admin: 'text-amber-400',
    viewer: 'text-sky-400',
    guest: 'text-emerald-400',
  };

  const roleLabels = {
    admin: 'Administrator',
    viewer: 'Viewer',
    guest: 'Guest',
  };

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="mobile-menu-btn"
        aria-label="Toggle menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={20} />
      </button>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`sidebar-wrap fixed top-0 left-0 h-full z-40 flex flex-col transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-[68px]' : 'w-[240px]'
        } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo */}
        <div className="sidebar-header flex-shrink-0">
          <div className="sidebar-logo">
            <img
              src="/assets/images/logo_USenghor_vertical_blanc_1000_min-1774525392061.png"
              alt="Université Senghor logo"
              className="w-8 h-8 object-contain"
            />
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <div className="sidebar-logo-text truncate">US Réservation</div>
              <div className="text-xs text-white/40 font-normal truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>Résidences</div>
            </div>
          )}
        </div>

        {/* Role badge */}
        {!isCollapsed && (
          <div className="px-3 py-2 mx-2 mb-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-2">
              <Icon
                name={role === 'admin' ? 'Shield' : role === 'viewer' ? 'Eye' : 'User'}
                size={13}
                className={roleColors?.[role] || 'text-white/50'}
              />
              <span className={`text-xs font-bold ${roleColors?.[role] || 'text-white/50'}`}>
                {roleLabels?.[role] || role}
              </span>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
          {navigationItems?.map((section, si) => (
            <div key={si}>
              {!isCollapsed && (
                <div className="nav-section-header">{section?.section}</div>
              )}
              <div className="mt-1 space-y-0.5">
                {section?.items?.map((item, ii) => (
                  <div
                    key={ii}
                    onClick={() => navigate(item?.path)}
                    className={`nav-item ${isActive(item?.path) ? 'active' : ''}`}
                    title={isCollapsed ? item?.label : undefined}
                  >
                    <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                    {!isCollapsed && (
                      <span className="text-sm font-medium truncate">{item?.label}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="flex-shrink-0 border-t p-2 space-y-1" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            className="nav-item w-full"
            title={isCollapsed ? (language === 'en' ? 'Français' : 'English') : undefined}
          >
            <span className="flex-shrink-0 text-sm font-bold w-[18px] text-center leading-none">
              {language === 'en' ? 'FR' : 'EN'}
            </span>
            {!isCollapsed && (
              <span className="text-sm font-medium">
                {language === 'en' ? 'Français' : 'English'}
              </span>
            )}
          </button>

          {/* Dark mode toggle */}
          <button
            onClick={toggleDark}
            className="nav-item w-full"
            title={isCollapsed ? (isDark ? t?.nav?.lightMode : t?.nav?.darkMode) : undefined}
          >
            <Icon name={isDark ? 'Sun' : 'Moon'} size={18} className="flex-shrink-0" />
            {!isCollapsed && <span className="text-sm font-medium">{isDark ? t?.nav?.lightMode : t?.nav?.darkMode}</span>}
          </button>

          {/* User info */}
          {!isCollapsed && user && (
            <div className="px-3 py-2">
              <div className="text-xs font-semibold text-white/80 truncate">{user?.user_metadata?.full_name || user?.email}</div>
              <div className={`text-xs font-bold mt-0.5 ${roleColors?.[role] || 'text-white/50'}`}>
                {roleLabels?.[role] || role}
              </div>
            </div>
          )}

          {/* Sign out */}
          <button
            onClick={handleSignOut}
            className="nav-item w-full hover:!bg-red-500/15 hover:!text-red-400"
            title={isCollapsed ? t?.nav?.signOut : undefined}
          >
            <Icon name="LogOut" size={18} className="flex-shrink-0" />
            {!isCollapsed && <span className="text-sm font-medium">{t?.nav?.signOut}</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;